import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUpRight, ArrowDownLeft, FileText, Quote, Receipt, Plus, Trash2 } from 'lucide-react';
import axios from '../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import { usePermissions } from '../hooks/usePermissions';
import { formatCurrency } from '../utils/currency';
import { formatDate, formatTime, formatDateTime, formatDateShort, formatDateLong, formatDateForInput } from '../utils/dateFormat';
import OutgoingPaymentForm from './accounting/OutgoingPaymentForm';
import IncomingPaymentForm from './accounting/IncomingPaymentForm';
import ManualTransactionForm from './accounting/ManualTransactionForm';
import QuotationList from './accounting/QuotationList';
import InvoiceList from './accounting/InvoiceList';
import AccountsPayable from './AccountsPayable';
import AccruedExpenses from './AccruedExpenses';

const Accounting = () => {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const { organization } = useOrganization();
  const { hasPermission } = usePermissions();
  
  // Determine default tab based on user permissions
  const getDefaultTab = () => {
    if (location.state?.activeTab) return location.state.activeTab;
    if (hasPermission('quotations.read')) return 'quotations';
    if (hasPermission('invoices.read')) return 'invoices';
    if (hasPermission('payments.read')) return 'incoming';
    if (hasPermission('accounts.read')) return 'accounts';
    return 'quotations'; // fallback
  };
  
  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [incomingPayments, setIncomingPayments] = useState([]);
  const [outgoingPayments, setOutgoingPayments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [accountFilter, setAccountFilter] = useState('');

  useEffect(() => {
    if (hasPermission('accounts.read')) {
      fetchAccounts();
    }
    if (activeTab === 'manual') {
      fetchTransactions();
    } else if (activeTab === 'incoming') {
      fetchIncomingPayments();
    } else if (activeTab === 'outgoing') {
      fetchOutgoingPayments();
    }
  }, [activeTab, hasPermission]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/accounts/chart-of-accounts');
      const accounts = response.data || [];
      setAccounts(accounts);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get('/api/transactions?type=MANUAL');
      setTransactions(response.data.transactions || []);
    } catch (error) {
      toast.error('Failed to fetch transactions');
    }
  };

  const fetchIncomingPayments = async () => {
    try {
      const response = await axios.get('/api/payments/incoming');
      setIncomingPayments(response.data.payments || []);
    } catch (error) {
      toast.error('Failed to fetch incoming payments');
    }
  };

  const fetchOutgoingPayments = async () => {
    try {
      const response = await axios.get('/api/expenses');
      const expenses = response.data.expenses || [];
      // Transform expenses to match outgoing payment format
      const transformedPayments = expenses.map(expense => ({
        id: expense.id,
        description: expense.description,
        amount: expense.amount,
        paymentDate: expense.expenseDate,
        paymentMethod: expense.expenseType === 'recurring' ? 'Recurring' : 'Manual',
        category: expense.category,
        createdAt: expense.createdAt,
        updatedAt: expense.updatedAt
      }));
      setOutgoingPayments(transformedPayments);
    } catch (error) {
      toast.error('Failed to fetch outgoing payments');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setShowAccountForm(false);
    fetchAccounts();
    if (activeTab === 'manual') {
      fetchTransactions();
    } else if (activeTab === 'incoming') {
      fetchIncomingPayments();
    } else if (activeTab === 'outgoing') {
      fetchOutgoingPayments();
    }
  };

  const handleCreateAccount = async (formData) => {
    try {
      await axios.post('/api/accounts', formData);
      toast.success('Account created successfully');
      handleFormSuccess();
    } catch (error) {
      toast.error('Failed to create account');
    }
  };

  const handleCreateDefaultAccounts = async () => {
    if (!window.confirm('This will create default accounts for all types. Continue?')) return;
    
    try {
      await axios.post('/api/accounts/create-defaults');
      toast.success('Default accounts created successfully');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to create default accounts');
    }
  };

  const handleDeleteAccount = async (account) => {
    try {
      // Check account usage first
      const response = await axios.get(`/api/accounts/${account.id}/usage`);
      const { 
        accountName, 
        transactionCount, 
        expenseCount, 
        isCritical, 
        hasBalance, 
        hasTransactions, 
        canDelete, 
        blockReasons 
      } = response.data;
      
      if (!canDelete) {
        let message = `Cannot delete account "${accountName}":\n\n`;
        blockReasons.forEach(reason => {
          message += `• ${reason}\n`;
        });
        
        if (isCritical) {
          alert(message + '\nSystem-critical accounts cannot be deleted.');
          return;
        }
        
        if (hasBalance) {
          alert(message + '\nPlease clear the account balance before deletion.');
          return;
        }
        
        if (hasTransactions) {
          const totalTransactions = transactionCount + expenseCount;
          message += `\nThis account has ${totalTransactions} linked transactions.\nYou can reassign them to another account of the same type.`;
          
          const reassignAccount = window.prompt(
            message + '\n\nEnter account ID to reassign transactions to (or leave empty to cancel):'
          );
          
          if (!reassignAccount) return;
          
          if (!window.confirm(`Reassign all transactions to account ID ${reassignAccount} and delete "${accountName}"?`)) return;
          
          await axios.delete(`/api/accounts/${account.id}?reassignTo=${reassignAccount}`);
          toast.success('Account deleted and transactions reassigned successfully');
        }
      } else {
        if (!window.confirm(`Delete account "${accountName}"?\n\nThis action cannot be undone.`)) return;
        
        await axios.delete(`/api/accounts/${account.id}`);
        toast.success('Account deleted successfully');
      }
      
      fetchAccounts();
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.isCritical) {
        toast.error(`Cannot delete system-critical account: ${errorData.accountName}`);
      } else if (errorData?.hasBalance) {
        toast.error(`Cannot delete account with balance: ${errorData.balance}`);
      } else {
        toast.error(errorData?.error || 'Failed to delete account');
      }
    }
  };

  const handleBatchDelete = async () => {
    if (!isAdmin || selectedItems.length === 0) return;
    if (!window.confirm(`Delete ${selectedItems.length} items?`)) return;
    
    try {
      const endpoint = activeTab === 'incoming' ? '/api/payments/incoming' :
                      activeTab === 'outgoing' ? '/api/payments/outgoing' : '/api/transactions';
      await Promise.all(selectedItems.map(id => axios.delete(`${endpoint}/${id}`)));
      
      if (activeTab === 'incoming') fetchIncomingPayments();
      else if (activeTab === 'outgoing') fetchOutgoingPayments();
      else fetchTransactions();
      
      setSelectedItems([]);
    } catch (error) {
    }
  };

  const AccountForm = ({ onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
      name: '',
      number: '',
      type: 'EXPENSE',
      balance: 0
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(formData);
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Create New Account</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Account Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Account Number</label>
            <input
              type="text"
              value={formData.number}
              onChange={(e) => setFormData({...formData, number: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              placeholder="e.g., 5001"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Account Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({...formData, type: e.target.value})}
              className="w-full px-3 py-2 border rounded-md"
              required
            >
              <option value="ASSET">ASSET (Cash/Bank)</option>
              <option value="EXPENSE">EXPENSE (Operating Expenses)</option>
              <option value="INCOME">INCOME (Revenue)</option>
              <option value="LIABILITY">LIABILITY (Debts)</option>
              <option value="EQUITY">EQUITY (Owner's Equity)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Initial Balance</label>
            <input
              type="number"
              step="0.01"
              value={formData.balance}
              onChange={(e) => setFormData({...formData, balance: parseFloat(e.target.value) || 0})}
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Create Account
            </button>
            <button type="button" onClick={onCancel} className="px-4 py-2 border rounded-md hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderForm = () => {
    switch (activeTab) {
      case 'accounts':
        return <AccountForm onSubmit={handleCreateAccount} onCancel={() => setShowAccountForm(false)} />;
      case 'outgoing':
        return <OutgoingPaymentForm accounts={accounts} onSuccess={handleFormSuccess} onCancel={() => setShowForm(false)} isExpenseMode={true} />;
      case 'incoming':
        return <IncomingPaymentForm accounts={accounts} onSuccess={handleFormSuccess} onCancel={() => setShowForm(false)} />;
      case 'manual':
        return <ManualTransactionForm accounts={accounts} onSuccess={handleFormSuccess} onCancel={() => setShowForm(false)} />;
      default:
        return null;
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'accounts':
        return showAccountForm ? renderForm() : (
          <div className="space-y-4">
            <div className="flex gap-4 items-center">
              <select
                value={accountFilter}
                onChange={(e) => setAccountFilter(e.target.value)}
                className="px-3 py-2 border rounded-md"
              >
                <option value="">All Types</option>
                <option value="ASSET">ASSET</option>
                <option value="LIABILITY">LIABILITY</option>
                <option value="EQUITY">EQUITY</option>
                <option value="INCOME">INCOME</option>
                <option value="EXPENSE">EXPENSE</option>
              </select>
              <button
                onClick={() => setShowAccountForm(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Account
              </button>
              <button
                onClick={handleCreateDefaultAccounts}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Default Accounts
              </button>
            </div>
            <div className="bg-white rounded-lg shadow border overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {accounts.filter(account => !accountFilter || account.type === accountFilter).map(account => (
                    <tr key={account.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{account.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{account.number}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          account.type === 'ASSET' ? 'bg-blue-100 text-blue-800' :
                          account.type === 'EXPENSE' ? 'bg-red-100 text-red-800' :
                          account.type === 'INCOME' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {account.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatCurrency(account.balance, organization?.currency_code)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <button
                          onClick={() => handleDeleteAccount(account)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete Account"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'quotations':
        return <QuotationList />;
      case 'invoices':
        return hasPermission('invoices.read') ? <InvoiceList /> : null;
      case 'payables':
        return <AccountsPayable />;
      case 'accrued':
        return <AccruedExpenses />;
      case 'outgoing':
      case 'incoming':
      case 'manual':
        return (showForm || showAccountForm) ? renderForm() : (
          <div className="space-y-4">
            <div className="flex gap-2">
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Record {activeTab === 'outgoing' ? 'Outgoing Payment' : activeTab === 'incoming' ? 'Incoming Payment' : 'Manual Transaction'}
              </button>
              {isAdmin && selectedItems.length > 0 && activeTab !== 'outgoing' && (
                <button
                  onClick={handleBatchDelete}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  🗑️ Delete ({selectedItems.length})
                </button>
              )}
            </div>

            <div className="bg-white rounded-lg shadow border">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold">Recent Transactions</h3>
              </div>
              <div className="divide-y">
                {activeTab === 'incoming' ? (
                  (incomingPayments || []).slice(0, 10).map((payment) => (
                    <div key={payment.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{payment.customer?.name || 'Unknown Customer'}</p>
                          <p className="text-sm text-gray-500">
                            {formatDate(payment.paymentDate)} • {payment.paymentMode}
                          </p>
                          <p className="text-sm text-gray-600">
                            {payment.invoice?.invoiceNumber || 'General Payment'}
                          </p>
                          {payment.notes && (
                            <p className="text-xs text-gray-400 mt-1">{payment.notes}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="text-green-600 font-medium">+{formatCurrency(parseFloat(payment.amount || 0), organization?.currency_code)}</span>
                          {payment.referenceNo && (
                            <p className="text-xs text-gray-400">Ref: {payment.referenceNo}</p>
                          )}
                          <button
                            onClick={() => {
                              const details = `INCOMING PAYMENT DETAILS\n` +
                                `═══════════════════════════════════\n` +
                                `Payment ID: ${payment.id}\n` +
                                `Date: ${formatDate(payment.paymentDate)} ${formatTime(payment.paymentDate)}\n` +
                                `Customer: ${payment.customer?.name || 'Unknown Customer'}\n` +
                                `Invoice: ${payment.invoice?.invoiceNumber || 'General Payment'}\n` +
                                `Amount: ${formatCurrency(parseFloat(payment.amount), organization?.currency_code)}\n` +
                                `Payment Method: ${payment.paymentMode}\n` +
                                `Reference No: ${payment.referenceNo || 'Not provided'}\n` +
                                `Notes: ${payment.notes || 'No notes'}\n` +
                                `Created: ${formatDate(payment.createdAt)} ${formatTime(payment.createdAt)}\n` +
                                `Updated: ${formatDate(payment.updatedAt)} ${formatTime(payment.updatedAt)}`;
                              alert(details);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-xs mt-1"
                            title="View Details"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : activeTab === 'outgoing' ? (
                  (outgoingPayments || []).slice(0, 10).map((payment) => (
                    <div key={payment.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{payment.description || 'Outgoing Payment'}</p>
                          <p className="text-sm text-gray-500">
                            {formatDate(payment.paymentDate)} • {payment.paymentMethod}
                          </p>
                          <p className="text-xs text-gray-400">
                            Category: {payment.category?.name || 'N/A'}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-red-600 font-medium">-{formatCurrency(parseFloat(payment.amount), organization?.currency_code)}</span>
                          <button
                            onClick={() => {
                              const details = `OUTGOING PAYMENT DETAILS\n` +
                                `═══════════════════════════════════\n` +
                                `Payment ID: ${payment.id}\n` +
                                `Date: ${formatDate(payment.paymentDate)} ${formatTime(payment.paymentDate)}\n` +
                                `Description: ${payment.description || 'Outgoing Payment'}\n` +
                                `Amount: ${formatCurrency(parseFloat(payment.amount), organization?.currency_code)}\n` +
                                `Payment Method: ${payment.paymentMethod}\n` +
                                `Category: ${payment.category?.name || 'N/A'}\n` +
                                `Type: ${payment.paymentMethod}\n` +
                                `Created: ${formatDate(payment.createdAt)} ${formatTime(payment.createdAt)}\n` +
                                `Updated: ${formatDate(payment.updatedAt)} ${formatTime(payment.updatedAt)}`;
                              alert(details);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-xs mt-1 block"
                            title="View Details"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  (transactions || []).slice(0, 10).map((transaction) => (
                    <div key={transaction.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium">{transaction.description}</p>
                          <p className="text-sm text-gray-500">
                            {formatDate(transaction.date)} • {transaction.type}
                          </p>
                          <p className="text-xs text-gray-400">Ref: {transaction.referencenumber}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            {transaction.debit > 0 && <span className="text-red-600 block">-{formatCurrency(transaction.debit, organization?.currency_code)}</span>}
                            {transaction.credit > 0 && <span className="text-green-600 block">+{formatCurrency(transaction.credit, organization?.currency_code)}</span>}
                          </div>
                          <button
                            onClick={() => alert(`Transaction Details:\nDate: ${formatDate(transaction.date)}\nAccount: ${transaction.account?.name}\nDescription: ${transaction.description}\nDebit: ${formatCurrency(transaction.debit, organization?.currency_code)}\nCredit: ${formatCurrency(transaction.credit, organization?.currency_code)}\nRef: ${transaction.referencenumber}`)}
                            className="text-blue-600 hover:text-blue-800"
                            title="View Details"
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Accounting</h1>
      </div>

      {/* Tabs */}
      <div className="border-b overflow-x-auto">
        <nav className="flex space-x-4 sm:space-x-8 min-w-max">
          {hasPermission('accounts.read') && (
            <button
              onClick={() => { setActiveTab('accounts'); setShowForm(false); setShowAccountForm(false); }}
              className={`py-2 px-1 border-b-2 flex items-center ${
                activeTab === 'accounts' ? 'border-indigo-500 text-indigo-600' : 'border-transparent'
              }`}
            >
              <FileText className="w-4 h-4 mr-1" />
              Accounts
            </button>
          )}
          {hasPermission('quotations.read') && (
            <button
              onClick={() => { setActiveTab('quotations'); setShowForm(false); setShowAccountForm(false); }}
              className={`py-2 px-1 border-b-2 flex items-center ${
                activeTab === 'quotations' ? 'border-blue-500 text-blue-600' : 'border-transparent'
              }`}
            >
              <Quote className="w-4 h-4 mr-1" />
              Quotations
            </button>
          )}
          {hasPermission('invoices.read') && (
            <button
              onClick={() => { setActiveTab('invoices'); setShowForm(false); setShowAccountForm(false); }}
              className={`py-2 px-1 border-b-2 flex items-center ${
                activeTab === 'invoices' ? 'border-purple-500 text-purple-600' : 'border-transparent'
              }`}
            >
              <Receipt className="w-4 h-4 mr-1" />
              Invoices
            </button>
          )}
          {hasPermission('payments.read') && (
            <button
              onClick={() => { setActiveTab('incoming'); setShowForm(false); setShowAccountForm(false); }}
              className={`py-2 px-1 border-b-2 flex items-center ${
                activeTab === 'incoming' ? 'border-green-500 text-green-600' : 'border-transparent'
              }`}
            >
              <ArrowDownLeft className="w-4 h-4 mr-1" />
              Incoming Payment
            </button>
          )}
          {hasPermission('payments.read') && (
            <button
              onClick={() => { setActiveTab('outgoing'); setShowForm(false); setShowAccountForm(false); }}
              className={`py-2 px-1 border-b-2 flex items-center ${
                activeTab === 'outgoing' ? 'border-red-500 text-red-600' : 'border-transparent'
              }`}
            >
              <ArrowUpRight className="w-4 h-4 mr-1" />
              Outgoing Payment
            </button>
          )}
          {hasPermission('accounts.read') && (
            <button
              onClick={() => { setActiveTab('manual'); setShowForm(false); setShowAccountForm(false); }}
              className={`py-2 px-1 border-b-2 flex items-center ${
                activeTab === 'manual' ? 'border-gray-500 text-gray-600' : 'border-transparent'
              }`}
            >
              <FileText className="w-4 h-4 mr-1" />
              Manual Transaction
            </button>
          )}
          {hasPermission('accounts.read') && (
            <button
              onClick={() => { setActiveTab('payables'); setShowForm(false); setShowAccountForm(false); }}
              className={`py-2 px-1 border-b-2 flex items-center ${
                activeTab === 'payables' ? 'border-orange-500 text-orange-600' : 'border-transparent'
              }`}
            >
              <FileText className="w-4 h-4 mr-1" />
              Accounts Payable
            </button>
          )}
          {hasPermission('expenses.read') && (
            <button
              onClick={() => { setActiveTab('accrued'); setShowForm(false); setShowAccountForm(false); }}
              className={`py-2 px-1 border-b-2 flex items-center ${
                activeTab === 'accrued' ? 'border-pink-500 text-pink-600' : 'border-transparent'
              }`}
            >
              <FileText className="w-4 h-4 mr-1" />
              Accrued Expenses
            </button>
          )}
        </nav>
      </div>

      {/* Content */}
      {['quotations', 'invoices', 'accounts', 'payables', 'accrued'].includes(activeTab) ? (
        renderContent()
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {renderContent()}
          </div>

          {/* Sidebar - Accounts */}
          <div className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold">Accounts</h3>
            </div>
            <div className="max-h-screen overflow-y-auto">
              {(accounts || []).map((account) => (
                <div key={account.id} className="p-3 border-b last:border-b-0">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-sm">{account.name}</p>
                      <p className="text-xs text-gray-500">{account.number}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${
                        account.balance >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formatCurrency(Math.abs(account.balance), organization?.currency_code)}
                      </p>
                      <p className="text-xs text-gray-500">{account.type}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounting;
